import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Loader2, RotateCw } from "lucide-react";
import { UpdateHistory } from "./UpdateHistory";
import { UpdateSteps } from "./UpdateSteps";
import { api, coachHeaders, formatInstall } from "../lib/api";

export const UpdatePanel = ({ code }) => {
  const [status, setStatus] = useState(null);
  const [unavailable, setUnavailable] = useState("");
  const [version, setVersion] = useState(null);
  const [suivi, setSuivi] = useState(false);
  const [note, setNote] = useState("");
  useEffect(() => { setNote(version?.pending_note ?? ""); }, [version?.pending_note]);
  const loadVersion = useCallback(() => { api.get("/version", coachHeaders(code)).then((r) => setVersion(r.data ?? null)).catch(() => {}); }, [code]);
  useEffect(() => { loadVersion(); }, [loadVersion]);
  const poll = useCallback(() => {
    api.get("/admin/update", coachHeaders(code))
      .then((r) => { setStatus(r.data ?? null); setUnavailable(""); if (r.data?.running) setSuivi(true); })
      .catch((e) => setUnavailable(e?.response?.data?.detail || "Indisponible"));
  }, [code]);
  useEffect(() => { poll(); }, [poll]);
  useEffect(() => {
    if (!status?.running) { loadVersion(); return undefined; }
    const t = setInterval(poll, 3000);
    return () => clearInterval(t);
  }, [status?.running, poll, loadVersion]);

  const lancer = async () => {
    if (!window.confirm("Mettre à jour le site avec la dernière version enregistrée sur GitHub ?\nLe site peut être indisponible quelques secondes.")) return;
    try {
      await api.put("/version/note", { note }, coachHeaders(code));
      await api.post("/admin/update", {}, coachHeaders(code)); toast.success("Mise à jour lancée"); setSuivi(true); setStatus({ running: true, log: [] }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Lancement impossible"); }
  };

  const log = Array.isArray(status?.log) ? status.log : [];
  const installe = formatInstall(version?.installed_at);
  const visible = suivi || status?.ok === false;
  const fini = visible && !status?.running && status?.ok !== false && log.some((l) => l.includes("Mise à jour terminée"));
  return (
    <div data-testid="update-panel" className="rounded-2xl border border-line bg-pitch p-4">
      <h2 className="font-display text-2xl uppercase text-chalk">Mise à jour du site</h2>
      {unavailable ? (
        <>
          <p data-testid="update-unavailable" className="mt-1 text-sm text-dim">{unavailable}</p>
          <p data-testid="update-current-version" className="mt-2 font-mono text-sm text-lime">Version {version?.version ?? "?"} - {version?.hash ?? "?"}</p>
          {installe && <p data-testid="update-installed-at" className="mt-1 text-xs text-dim">Dernière {installe}</p>}
          <UpdateHistory history={version?.history} code={code} onSaved={loadVersion} />
        </>
      ) : (
        <>
          <p className="mt-1 text-sm text-dim">Après « Save to GitHub » sur Emergent, clique ici pour installer la nouvelle version.</p>
          <label htmlFor="update-note" className="mt-3 block text-xs font-semibold uppercase tracking-widest text-dim">Note de version (facultatif)</label>
          <input id="update-note" data-testid="update-note-input" value={note} maxLength={200} disabled={status?.running}
            onChange={(e) => setNote(e.target.value)} placeholder="Ex. : ajout de l'heure limite de réponse"
            className="mt-1 w-full rounded-xl border border-line bg-ink px-3 py-2 text-sm text-chalk outline-none focus:border-lime disabled:opacity-60" />
          <button data-testid="update-start-button" onClick={lancer} disabled={status?.running} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-lime px-5 py-3 font-semibold text-ink disabled:opacity-60">
            {status?.running ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            {status?.running ? "Mise à jour en cours…" : "Mettre à jour le site"}
          </button>
          <p data-testid="update-current-version" className="mt-2 text-center font-mono text-sm text-lime">Version {version?.version ?? "?"} - {version?.hash ?? "?"}</p>
          {installe && <p data-testid="update-installed-at" className="mt-1 text-center text-xs text-dim">Dernière {installe}</p>}
          {fini && (
            <div data-testid="update-done" className="mt-3 rounded-xl border border-lime/40 bg-ink p-3 text-center">
              <p className="text-sm text-lime">Mise à jour terminée.</p>
              <button data-testid="update-reload-button" onClick={() => window.location.reload()}
                className="mt-2 inline-flex items-center gap-2 rounded-full border border-lime px-5 py-2 text-sm font-semibold text-lime transition-colors hover:bg-lime hover:text-ink">
                <RotateCw className="h-4 w-4" /> Recharger la page
              </button>
            </div>
          )}
          {visible && <UpdateSteps steps={status?.steps} log={log} running={status?.running} ok={status?.ok} />}
          <UpdateHistory history={version?.history} code={code} onSaved={loadVersion} />
        </>
      )}
    </div>
  );
};
